import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { useAuthStore } from '../../stores/useAuthStore';
import {
  GalleryIcon, UserIcon, MoodboardIcon, BatchIcon, StillsIcon,
  EditorIcon, VideoIcon, TrendForgeIcon, WorkflowIcon
} from '../icons/Icons';
import { canUseMarketingSegment, getSegment } from '../../utils/segment';

interface MobileAppsMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileAppsMenu: React.FC<MobileAppsMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const apps = [
    { icon: <MoodboardIcon className="w-6 h-6" />, label: 'Canvas', route: '/moodboard' },
    { icon: <WorkflowIcon className="w-6 h-6" />, label: 'Workflow', route: '/workflow' },
    { icon: <BatchIcon className="w-6 h-6" />, label: 'Lookbook', route: '/batch' },
    { icon: <StillsIcon className="w-6 h-6" />, label: 'Stills Vestuário', route: '/stills/vestuario' },
    { icon: <StillsIcon className="w-6 h-6" />, label: 'Stills Produtos', route: '/stills/produtos' },
    { icon: <VideoIcon className="w-6 h-6" />, label: 'Vídeos', route: '/video' },
    { icon: <EditorIcon className="w-6 h-6" />, label: 'Editor', route: '/editor' },
    { icon: <UserIcon className="w-6 h-6" />, label: 'Modelos', route: '/models/create' },
    { icon: <TrendForgeIcon className="w-6 h-6" />, label: 'TrendForge', route: '#' },
  ];

  return (
    <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-snapfy-surface rounded-t-3xl border-t border-snapfy-border p-6 animate-in slide-in-from-bottom-full duration-300">
        <div className="w-12 h-1.5 bg-snapfy-border rounded-full mx-auto mb-6" />
        <h3 className="text-lg font-bold text-snapfy-text-primary mb-4">Criar com IA</h3>
        <div className="grid grid-cols-4 gap-4">
          {apps.map((app) => (
            <button
              key={app.label}
              onClick={() => {
                if (app.route !== '#') navigate(app.route);
                onClose();
              }}
              className="flex flex-col items-center gap-2"
            >
              <div className="w-14 h-14 rounded-2xl bg-snapfy-inner border border-snapfy-border flex items-center justify-center text-snapfy-text-primary hover:border-[#7063f7] hover:text-[#7063f7] transition-all">
                {app.icon}
              </div>
              <span className="text-[10px] font-medium text-snapfy-text-secondary">{app.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const { unreadCount } = useNotificationStore();
  const { isAuthenticated } = useAuthStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAppsMenuOpen, setIsAppsMenuOpen] = useState(false);
  const dragStartY = useRef<number | null>(null);
  const user = useAuthStore((s) => s.user);

  // Barra do Fashion: no segmento Marketing o menu abre pela barra superior.
  if (getSegment(location.pathname) === 'marketing' && canUseMarketingSegment(user)) return null;

  return (
    <>
      {/* Bottom Nav Bar — mobile only */}
      <div
        className={`lg:hidden fixed bottom-4 left-4 right-4 h-16 snapfy-glass-strong rounded-glass z-40 flex items-center justify-around px-2 shadow-2xl transition-transform duration-300 ease-in-out ${
          isCollapsed ? 'translate-y-[calc(100%+32px)]' : 'translate-y-0'
        }`}
      >
        {/* Drag Handle */}
        <div
          className="absolute -top-11 left-1/2 -translate-x-1/2 w-24 h-6 flex justify-center items-center cursor-grab active:cursor-grabbing z-50"
          onClick={() => setIsCollapsed(!isCollapsed)}
          onTouchStart={(e) => { dragStartY.current = e.touches[0].clientY; }}
          onTouchMove={(e) => {
            if (dragStartY.current === null) return;
            const deltaY = e.touches[0].clientY - dragStartY.current;
            if (isCollapsed && deltaY < -20) {
              setIsCollapsed(false);
              dragStartY.current = null;
            } else if (!isCollapsed && deltaY > 20) {
              setIsCollapsed(true);
              dragStartY.current = null;
            }
          }}
          onTouchEnd={() => { dragStartY.current = null; }}
        >
          <div className="w-12 h-1.5 bg-snapfy-text-secondary/50 rounded-full shadow-sm" />
        </div>

        {/* Menu (open sidebar) — we use a link to trigger mobile sidebar instead */}
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            // Dispatch custom event to open mobile sidebar
            window.dispatchEvent(new CustomEvent('open-mobile-sidebar'));
          }}
          className="p-2 text-snapfy-text-secondary hover:text-snapfy-text-primary flex flex-col items-center gap-1"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </Link>

        {/* Gallery */}
        <Link
          to="/gallery"
          className={`p-2 flex flex-col items-center gap-1 ${
            location.pathname === '/gallery' ? 'text-[#7063f7]' : 'text-snapfy-text-secondary hover:text-snapfy-text-primary'
          }`}
        >
          <GalleryIcon className="w-6 h-6" />
        </Link>

        {/* Center FAB (+) */}
        <div className="relative -top-5">
          <button
            onClick={() => setIsAppsMenuOpen(true)}
            className="w-14 h-14 bg-[#7063f7] rounded-full flex items-center justify-center text-white shadow-lg shadow-[#7063f7]/30 border-4 border-snapfy-bg"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {/* Notifications */}
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('open-notifications'));
          }}
          className="p-2 text-snapfy-text-secondary hover:text-snapfy-text-primary relative flex flex-col items-center gap-1"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {isAuthenticated && unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-[#7063f7] rounded-full border-2 border-snapfy-surface" />
          )}
        </Link>

        {/* Profile */}
        <Link
          to="/profile"
          className={`p-2 flex flex-col items-center gap-1 ${
            location.pathname === '/profile' ? 'text-[#7063f7]' : 'text-snapfy-text-secondary hover:text-snapfy-text-primary'
          }`}
        >
          <UserIcon className="w-6 h-6" />
        </Link>
      </div>

      {/* Apps Menu Bottom Sheet */}
      <MobileAppsMenu isOpen={isAppsMenuOpen} onClose={() => setIsAppsMenuOpen(false)} />
    </>
  );
};

export default MobileBottomNav;
